import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { NAV_LINKS, WHATSAPP_MSG_URL } from "./data";
import { scrollToId, useLenis } from "./lenis-context";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  const go = (id) => {
    setOpen(false);
    scrollToId(lenis, id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-sapphire/95 shadow-lg shadow-sapphire-ink/20 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20 lg:px-8">
        <button
          data-testid="nav-brand-logo"
          onClick={() => go("home")}
          className="flex items-center text-left"
          aria-label="swaclean Facility Services — home"
        >
          <img src="/logo.png" alt="swaclean Facility Services" className="h-9 w-auto md:h-11" />
        </button>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => go(l.id)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                l.id === "book"
                  ? "ml-2 bg-white text-sapphire hover:bg-sapphire-light"
                  : "text-white/85 hover:bg-white/10 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-whatsapp-cta-button"
            href={WHATSAPP_MSG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-whatsapp px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-whatsapp/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-whatsapp-dark sm:inline-flex"
          >
            <MessageCircle className="h-4 w-4" />
            +91 90649 19287
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-sapphire lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-4 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testId}`}
                  onClick={() => go(l.id)}
                  className="block w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-white/90 transition-colors hover:bg-white/10"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-whatsapp-cta-button"
                href={WHATSAPP_MSG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-whatsapp px-4 py-3 text-sm font-bold text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
